import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { GitBranch, ExternalLink, ArrowDownToLine, Layers, ArrowUpFromLine, X, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";

const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-");

function NodeCard({ n, focused, dimmed, onFocus, refCb, sub }) {
  const navigate = useNavigate();
  return (
    <div
      ref={refCb}
      role="button"
      tabIndex={0}
      onClick={() => onFocus(n.id)}
      onKeyDown={(e) => e.key === "Enter" && onFocus(n.id)}
      data-testid={`dataflow-node-${slug(n.id)}`}
      className={`border rounded-sm px-3 py-2 cursor-pointer transition-all bg-card ${
        focused ? "border-accent bg-surface-hover" : "border-border hover:border-accent/50"
      } ${dimmed ? "opacity-25" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium truncate">{n.label}</span>
        <button
          type="button"
          title={`Open ${n.label}`}
          data-testid={`dataflow-open-${slug(n.id)}`}
          onClick={(e) => { e.stopPropagation(); navigate(n.route); }}
          className="text-muted-foreground hover:text-accent shrink-0"
        >
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      {sub && <div className="text-[10px] text-muted-foreground truncate mt-0.5">{sub}</div>}
    </div>
  );
}

function IntegrityDigestCard() {
  const [digest, setDigest] = useState(null);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/provenance/integrity/digest").then((r) => setDigest(r.data.digest)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/provenance/integrity/digest/run");
      toast.success(`Checks run — ${r.data.divergences} divergence(s)${r.data.new_divergences ? `, ${r.data.new_divergences} new (bell alerted)` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run checks"); }
    setRunning(false);
  };
  const divergent = (digest?.checks || []).filter((c) => !c.consistent);
  return (
    <div className="card-flat p-4" data-testid="integrity-digest-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {digest && !digest.consistent
            ? <ShieldAlert className="h-4 w-4 text-red-400" />
            : <ShieldCheck className="h-4 w-4 text-[#00E65B]" />}
          <span className="font-display font-semibold text-sm">Integrity digest</span>
          {digest && (
            <Badge variant={digest.consistent ? "success" : "danger"} className="text-[9px]" data-testid="integrity-digest-status">
              {digest.consistent ? "all surfaces consistent" : `${digest.divergences} divergence${digest.divergences === 1 ? "" : "s"}`}
            </Badge>
          )}
          {digest?.run_at && <span className="text-[10px] font-mono text-muted-foreground">last run {digest.run_at.slice(0, 16).replace("T", " ")} UTC</span>}
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={run} disabled={running} data-testid="integrity-digest-run-btn">
          <RefreshCw className={`h-3 w-3 ${running ? "animate-spin" : ""}`} /> {running ? "Running…" : "Run checks now"}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">
        All Control Tower cross-checks run nightly for your organisation ({digest ? digest.checks.length : "…"} checks). The bell is alerted only when a surface <em>newly</em> starts diverging.
      </p>
      {divergent.length > 0 && (
        <div className="mt-2 space-y-1">
          {divergent.map((c, i) => (
            <div key={i} className="border border-red-500/50 bg-red-500/5 rounded-sm px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2 flex-wrap" data-testid={`integrity-digest-div-${i}`}>
              <span className="font-medium">{c.quantity}</span>
              <span className="font-mono">{c.a.surface}: <b className="text-red-400">{c.a.value != null ? Number(c.a.value).toLocaleString() : "—"}</b> vs {c.b.surface}: <b className="text-red-400">{c.b.value != null ? Number(c.b.value).toLocaleString() : "—"}</b></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DataFlowMap() {
  const [d, setD] = useState(null);
  const [focus, setFocus] = useState(null);
  const wrapRef = useRef(null);
  const nodeRefs = useRef({});
  const [pos, setPos] = useState({});

  useEffect(() => { api.get("/provenance/graph/map").then((r) => setD(r.data)).catch(() => {}); }, []);

  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const wr = wrap.getBoundingClientRect();
    const p = {};
    Object.entries(nodeRefs.current).forEach(([id, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      p[id] = { left: r.left - wr.left, right: r.right - wr.left, y: r.top - wr.top + r.height / 2 };
    });
    setPos(p);
  }, []);
  useLayoutEffect(() => { measure(); }, [d, measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const edges = useMemo(() => (d ? [...d.edges_up, ...d.edges_down] : []), [d]);
  const connected = useMemo(() => {
    if (!focus) return null;
    const s = new Set([focus]);
    edges.forEach((e) => {
      if (e.from === focus) s.add(e.to);
      if (e.to === focus) s.add(e.from);
    });
    return s;
  }, [focus, edges]);

  const toggleFocus = (id) => setFocus((f) => (f === id ? null : id));
  const dim = (id) => Boolean(connected && !connected.has(id));

  return (
    <div className="space-y-6" data-testid="dataflow-page">
      <IntegrityDigestCard />
      <div>
        <div className="label-eyebrow mb-1">Provenance · Platform transparency</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
          <GitBranch className="h-8 w-8 text-accent" /> Data Flow Map
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          How data moves through Lestari: capture channels feed platform modules, whose figures are consumed by
          reports, registers and downstream analytics. Click any node to trace its connections; use the open icon to jump to that module.
        </p>
        {d && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Badge variant="outline" className="text-[10px] font-mono" data-testid="dataflow-stats">
              {d.sources.length} inputs → {d.modules.length} modules → {d.consumers.length} consumers · {d.widget_count} curated widget definitions
            </Badge>
            {focus && (
              <button
                onClick={() => setFocus(null)}
                data-testid="dataflow-clear-focus"
                className="text-[10px] font-mono text-accent inline-flex items-center gap-1 border border-accent/40 rounded-sm px-2 py-0.5 hover:bg-surface-hover"
              >
                <X className="h-3 w-3" /> clear trace
              </button>
            )}
          </div>
        )}
      </div>

      {!d && <div className="text-sm text-muted-foreground animate-pulse">Mapping the platform…</div>}

      {d && (
        <div ref={wrapRef} className="relative">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" data-testid="dataflow-edges">
            {edges.map((e, i) => {
              const a = pos[e.from];
              const b = pos[e.to];
              if (!a || !b) return null;
              const x1 = a.right, x2 = b.left;
              const mx = (x2 - x1) / 2;
              const active = focus && (e.from === focus || e.to === focus);
              const hidden = focus && !active;
              return (
                <path
                  key={i}
                  d={`M ${x1} ${a.y} C ${x1 + mx} ${a.y}, ${x2 - mx} ${b.y}, ${x2} ${b.y}`}
                  fill="none"
                  stroke={active ? "hsl(var(--accent))" : "currentColor"}
                  className={active ? "" : "text-border"}
                  strokeWidth={active ? 1.6 : Math.min(2.5, 0.6 + e.weight * 0.15)}
                  opacity={hidden ? 0.06 : active ? 0.9 : 0.45}
                />
              );
            })}
          </svg>
          <div className="grid grid-cols-3 gap-16 relative">
            <div className="space-y-2">
              <div className="label-eyebrow text-[10px] mb-3 flex items-center gap-1.5">
                <ArrowDownToLine className="h-3 w-3" /> Capture & inputs
              </div>
              {d.sources.map((n) => (
                <NodeCard key={n.id} n={n} focused={focus === n.id} dimmed={dim(n.id)} onFocus={toggleFocus}
                  refCb={(el) => (nodeRefs.current[n.id] = el)} sub={n.desc} />
              ))}
            </div>
            <div className="space-y-2">
              <div className="label-eyebrow text-[10px] mb-3 flex items-center gap-1.5">
                <Layers className="h-3 w-3" /> Platform modules
              </div>
              {d.modules.map((n) => (
                <NodeCard key={n.id} n={n} focused={focus === n.id} dimmed={dim(n.id)} onFocus={toggleFocus}
                  refCb={(el) => (nodeRefs.current[n.id] = el)} sub={`${n.widgets} covered widget${n.widgets === 1 ? "" : "s"}`} />
              ))}
            </div>
            <div className="space-y-2">
              <div className="label-eyebrow text-[10px] mb-3 flex items-center gap-1.5">
                <ArrowUpFromLine className="h-3 w-3" /> Consumed by
              </div>
              {d.consumers.map((n) => (
                <NodeCard key={n.id} n={n} focused={focus === n.id} dimmed={dim(n.id)} onFocus={toggleFocus}
                  refCb={(el) => (nodeRefs.current[n.id] = el)} sub={n.desc} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
